import { addBusinessMinutes } from './business-hours';
import type { ScheduleSlot } from './business-hours';

/**
 * SLA goal/deadline math — PURE (spec v3 §4). Vive no shared porque MAIS DE UM
 * módulo precisa da mesma regra (tickets recalcula em create/patch/resume; o
 * adapter de automações recalcula no set_priority) e o front deriva metas —
 * duplicar a precedência item>prioridade foi vetado em review.
 */

/** Attend/solve goals in business minutes; null = no goal for that leg. */
export interface SlaGoals {
  attendMinutes: number | null;
  solveMinutes: number | null;
}

/** Anything exposing SLA minutes (catalog item or priority rows). */
export interface SlaGoalSource {
  attendMinutes: number | null;
  solveMinutes: number | null;
}

/**
 * Goal resolution: the catalog ITEM wins when the ticket has one and the item
 * defines the minutes; the priority is the per-leg fallback (spec §4).
 */
export function resolveSlaGoals(
  catalogItem: SlaGoalSource | null,
  priority: SlaGoalSource | null
): SlaGoals {
  return {
    attendMinutes: catalogItem?.attendMinutes ?? priority?.attendMinutes ?? null,
    solveMinutes: catalogItem?.solveMinutes ?? priority?.solveMinutes ?? null,
  };
}

// ── Estado de um alvo de SLA (v3; no shared desde a spec 018) ───────────────
/**
 * Estados de UM alvo (atendimento ou solução). Morava só no web; a spec 018
 * passou a contar "Prazo em risco" na API (indicadores de Hoje e visões da
 * lista) e a tela e o servidor não podem discordar sobre o mesmo ticket — a
 * regra agora é uma só, aqui.
 */
export type SlaState = 'paused' | 'met' | 'overdue' | 'due_soon' | 'on_track';

/** Fração restante da janela abaixo da qual o alvo vira "A vencer". */
export const SLA_DUE_SOON_FRACTION = 0.25;

/**
 * Estado de UM alvo de SLA. `null` = sem meta. Precedência: Cumprido > Pausado
 * > Vencido > A vencer > Em dia — um alvo já cumprido não volta a ser
 * "pausado", e um SLA pausado não "vence". A janela começa no createdAt do
 * ticket (os prazos são computados dele).
 */
export function slaTargetState(input: {
  dueAt: string | Date | null;
  doneAt: string | Date | null;
  pausedAt: string | Date | null;
  startAt: string | Date;
  now: Date;
}): SlaState | null {
  if (!input.dueAt) return null;
  if (input.doneAt) return 'met';
  if (input.pausedAt) return 'paused';
  const due = new Date(input.dueAt).getTime();
  const now = input.now.getTime();
  if (now > due) return 'overdue';
  const windowMs = due - new Date(input.startAt).getTime();
  if (windowMs > 0 && due - now < windowMs * SLA_DUE_SOON_FRACTION) return 'due_soon';
  return 'on_track';
}

export interface ComputeSlaDuesInput {
  createdAt: Date;
  goals: SlaGoals;
  /** business minutes accumulated from already-resumed pauses */
  slaPausedMinutes: number;
  slots: ScheduleSlot[];
  holidays: string[];
}

/**
 * dueAt = addBusinessMinutes(createdAt, goalMinutes + slaPausedMinutes, ...).
 * A null goal produces a null due (no active SLA for that leg).
 */
export function computeSlaDues(input: ComputeSlaDuesInput): {
  attendDueAt: Date | null;
  solveDueAt: Date | null;
} {
  const due = (minutes: number | null): Date | null =>
    minutes === null
      ? null
      : addBusinessMinutes(
          input.createdAt,
          minutes + input.slaPausedMinutes,
          input.slots,
          input.holidays
        );
  return {
    attendDueAt: due(input.goals.attendMinutes),
    solveDueAt: due(input.goals.solveMinutes),
  };
}
